import { Logger } from 'wdc-cube';
import { createHistoryCodec } from 'wdc-cube/codec';
import { ApiClient } from './api-client';
const LOG = Logger.get('Mormaii.HistoryCodec');
/**
 * Quem decide se o endereco viaja cifrado, e com qual chave.
 *
 * O endereco carrega filtro, e filtro carrega CPF (`cpf` na lista de colaboradores,
 * `mon-cpf` nos monitoramentos). Isso fica no historico do navegador, que sincroniza
 * entre dispositivos, aparece em captura de tela e vai junto num link colado.
 *
 * ## So a area restrita e selada
 *
 * A area publica NAO ganha codec, e a decisao e deliberada. A unica coisa que viaja
 * ali e o `codigo` do historico compartilhado, que ja E a credencial: quem tem o
 * link tem o acesso, e selar com uma chave que mora no bundle nao esconderia nada de
 * quem baixasse a aplicacao. O que se ganharia e ilegibilidade; o que se perderia e
 * um link que o proprio backend monta e manda por e-mail.
 *
 * ## A chave
 *
 * Vem do servidor, por usuario, derivada por HKDF de um segredo mestre que nunca sai
 * de la. E estavel no tempo, entao um endereco copiado hoje abre daqui a seis meses,
 * e e a mesma em qualquer dispositivo onde a pessoa logue.
 *
 * Guardada no `sessionStorage`, e nao no `localStorage`: ela nao pode sobreviver ao
 * fechar do navegador numa maquina compartilhada. O custo e que cada aba nova paga
 * uma ida ao servidor, que e o preco certo.
 */
const KEY_STORAGE = '@galileo:history-key';
const client = ApiClient.INSTANCE;
/**
 * A chave da AREA PUBLICA, fixa neste pacote.
 *
 * Isto e OFUSCACAO, e nao segredo: quem baixa a aplicacao tem a chave, e com ela
 * consegue produzir um endereco valido. O que ela compra e real, mas e menor do que
 * parece - o endereco deixa de ser legivel no historico, na captura de tela e por
 * cima do ombro, e deixa de ser EDITAVEL a mao: a cifra autentica, entao mexer num
 * caractere faz o endereco ser recusado inteiro em vez de chegar meio trocado.
 *
 * O que ela NAO compra: autenticidade contra quem se disponha a ler o bundle. Para
 * isso a chave teria de ser secreta, e uma chave secreta no navegador nao existe.
 *
 * Fixa tambem significa COMPARTILHAVEL, que e requisito aqui: o historico de um
 * paciente e aberto por link, e o link tem de funcionar para quem o recebe.
 */
const PUBLIC_KEY = 'UEmcltpoM_Q_ef-LrTb1fvjs04Ph5Q71jKN3nKaLmbQ';
/**
 * A versao da chave publica, distante da do usuario (que o servidor emite comecando
 * em 1) para que um envelope de um regime falhe LIMPO no outro, em vez de tentar
 * decifrar com a chave errada.
 */
const PUBLIC_VERSION = 100;
let publicCodecCache;
/**
 * O codec do usuario, guardado JUNTO COM A CHAVE que o montou.
 *
 * Guardar so o codec faria uma chave nova - outra pessoa, ou a mesma depois de uma
 * rotacao - continuar usando o codec da anterior. O cache existe para que
 * `sealAddress` possa comparar por identidade e nao republicar o endereco a cada
 * navegacao; atrela-lo a chave e o que o mantem honesto.
 */
let userCodecCache;
/**
 * O codec da area publica: chave fixa, e o mesmo para todo mundo.
 */
export function publicHistoryCodec() {
    if (!publicCodecCache) {
        try {
            publicCodecCache = createHistoryCodec({
                current: { version: PUBLIC_VERSION, key: PUBLIC_KEY }
            });
        }
        catch (caught) {
            LOG.warn('Nao foi possivel montar o codec publico', caught);
            return undefined;
        }
    }
    return publicCodecCache;
}
/**
 * O codec da sessao corrente, ou `undefined` quando nao ha o que selar.
 *
 * `undefined` acontece em tres casos, e nenhum deles e erro: ninguem logado, o
 * servidor sem o segredo mestre (responde 503), ou um servidor mais antigo que o
 * endpoint. Nos tres o painel segue publicando o endereco em texto claro, que e o
 * que ele ja fazia.
 */
export async function currentHistoryCodec() {
    try {
        return (await userHistoryCodec()) ?? publicHistoryCodec();
    }
    catch (caught) {
        /*
         * Sessao guardada que o servidor ja nao aceita - vencida, ou derrubada pela
         * sessao unica. A chave da pessoa nao vem, e o 403 subia ate o arranque, que
         * ficava SEM cifra nenhuma: o endereco que a ponte do arranque selou com a chave
         * publica, o do historico compartilhado, deixava de ser lido, e a tela dizia
         * "Link invalido ou expirado" para um link valido.
         */
        LOG.warn('Chave da pessoa indisponivel: o endereco segue com a chave publica', caught);
        return publicHistoryCodec();
    }
}
/**
 * O codec da area restrita: chave desta pessoa, emitida pelo servidor.
 *
 * Ele tambem LE o que foi selado com a chave publica, por isso ela entra em
 * `previous`. Sem isso, abrir um historico compartilhado ja logado - endereco
 * publico, selado com a chave publica - falharia ao decifrar, e os parametros se
 * perderiam no caminho. A reciproca nao vale, e nao deve valer: o codec publico nao
 * tem como ler o que e de uma pessoa so.
 *
 * `undefined` quando nao ha sessao, quando o servidor nao tem o segredo mestre
 * (responde 503) ou quando ele e mais antigo que o endpoint.
 */
export async function userHistoryCodec() {
    const material = await currentHistoryKey();
    if (!material) {
        userCodecCache = undefined;
        return undefined;
    }
    if (userCodecCache?.key === material.key) {
        return userCodecCache.codec;
    }
    try {
        const codec = createHistoryCodec({
            current: { version: material.version, key: material.key },
            previous: [{ version: PUBLIC_VERSION, key: PUBLIC_KEY }]
        });
        userCodecCache = { key: material.key, codec };
        return codec;
    }
    catch (caught) {
        // Chave com tamanho errado, ou peer ausente no bundle. Preferimos o endereco
        // legivel a uma aplicacao que nao sobe.
        LOG.warn('Nao foi possivel montar o codec do endereco', caught);
        return undefined;
    }
}
/**
 * Sela um endereco que vai ser publicado FORA da barra de enderecos.
 *
 * O caso e o `href` de um link: a lista de colaboradores desenha `<a>` de verdade
 * para que o botao do meio abra o detalhe em outra aba, e o endereco ali e escrito
 * a mao em vez de sair do `HistoryManager` - entao ninguem o cifra por nos. Sem
 * isto ele sai em texto claro, e o `SealedHistoryManager` o RECUSA na chegada: o
 * clique levava ao detalhe sem paciente algum.
 *
 * Sem codec, ou sem query, devolve o que recebeu - que e o comportamento certo
 * quando a aplicacao esta rodando em texto claro.
 */
export function sealAddress(plain, codec) {
    const cut = plain.indexOf('?');
    if (!codec || cut < 0) {
        return plain;
    }
    return `${plain.slice(0, cut)}?${codec.envelope}=${codec.encode(plain.slice(cut + 1))}`;
}
/**
 * Abre um endereco que chegou selado, agora que existe chave.
 *
 * Serve a UM caso, e ele importa: alguem cola no navegador deslogado um link de
 * tela interna que foi copiado ja cifrado. Sem sessao nao ha chave, entao o
 * `_e=...` atravessa a porta como um parametro qualquer - o `FlipIntent` o
 * preserva - e chega intacto ao `next` do login. Depois de entrar, ha chave, e e
 * aqui que ele se abre.
 *
 * Sem isto o link ainda levaria a tela certa, mas com os filtros perdidos, o que
 * seria uma regressao: hoje, sem cifra alguma, ele leva com tudo.
 *
 * O codec vive no `HistoryManager` e so atua sobre o endereco do navegador; este
 * `next` e uma string que viaja DENTRO de outro endereco, entao ninguem o
 * decifraria por nos.
 */
export function unsealAddress(address, codec) {
    const cut = address.indexOf('?');
    if (cut < 0) {
        return address;
    }
    const path = address.slice(0, cut);
    const query = address.slice(cut + 1);
    const prefix = `${codec.envelope}=`;
    if (!query.startsWith(prefix) || query.includes('&')) {
        // Nao e um envelope: ou veio em texto claro, ou e uma mistura que este
        // codec nao produz. Fica como esta.
        return address;
    }
    const opened = codec.decode(query.slice(prefix.length));
    if (opened === undefined) {
        // Chave errada, versao que este build nao le, ou adulterado. Levar a tela
        // sem filtro e melhor que nao levar.
        LOG.info('Endereco selado que nao abriu: seguindo sem os parametros');
        return path;
    }
    return opened ? `${path}?${opened}` : path;
}
/**
 * A regra do endereco aceito, separada de quem le a barra do navegador.
 *
 * Fica aqui, pura, porque a suite da apresentacao roda SEM DOM de proposito - e essa
 * escolha e o que prova que a camada nao conhece view alguma. Uma regra presa a uma
 * subclasse de `PageHistoryManager` so poderia ser conferida no navegador.
 *
 * @param claro o endereco como a base ja o entrega, decifrado
 * @param rawQuery a query como esta na barra, antes de decifrar
 * @param selado se aquela query e um envelope desta aplicacao
 */
export function keepOnlySealed(plain, rawQuery, sealed) {
    // Sem query nao ha parametro para recusar, e e a maioria dos enderecos. Com
    // envelope, o framework ja fez o trabalho - inclusive descartar o que nao
    // conseguiu decifrar.
    if (!rawQuery || sealed) {
        return plain;
    }
    const cut = plain.indexOf('?');
    return cut < 0 ? plain : plain.slice(0, cut);
}
/** Esquece a chave guardada, e o codec que ela montou. Chamado ao sair. */
export function forgetHistoryKey() {
    userCodecCache = undefined;
    try {
        sessionStorage.removeItem(KEY_STORAGE);
    }
    catch {
        /* nada a fazer */
    }
}
/**
 * A chave com que ESTE usuario cifra o que viaja na URL.
 *
 * Mora aqui, e nao num servico geral, porque a chave so existe para a cifra do
 * endereco - e quem decide a cifra e este modulo.
 *
 * Vem de um endpoint proprio, e nao da resposta do login, porque o login nao se
 * repete: recarregar numa aba nova mantem a sessao (ela vive no `localStorage`) e
 * nao traz de volta o corpo do sign-in. E a chave precisa existir ANTES do
 * `kickStart`, que e quem le o endereco.
 *
 * `undefined` quando o servidor responde 503, que e o que ele diz quando o segredo
 * mestre nao esta configurado. Nesse caso o painel segue com o endereco em texto
 * claro - que e o que ele ja faz hoje.
 */
export async function fetchHistoryKey() {
    if (!client.session) {
        return undefined;
    }
    try {
        return (await client.send('GET', '/auth/history_key'));
    }
    catch (caught) {
        const status = caught?.status;
        if (status === 503 || status === 404) {
            // Servidor sem o segredo, ou mais antigo que o endpoint. Nenhum dos
            // dois e motivo para a aplicacao nao subir.
            LOG.info('Sem chave de historico: o endereco segue em texto claro');
            return undefined;
        }
        throw caught;
    }
}
async function currentHistoryKey() {
    // `restoreStoredSession` e nao `session`: no arranque o `postConstruct` do
    // servico ainda nao rodou, e sem sessao em maos a requisicao sairia sem o
    // Authorization - o codec nunca seria instalado num F5.
    if (!client.restoreStoredSession()) {
        return undefined;
    }
    const stored = readStoredKey();
    if (stored) {
        return stored;
    }
    const material = await fetchHistoryKey();
    if (material) {
        storeKey(material);
    }
    return material;
}
function readStoredKey() {
    let raw = null;
    try {
        raw = sessionStorage.getItem(KEY_STORAGE);
    }
    catch {
        // Aba privada, site data bloqueado. Buscar de novo custa uma requisicao.
        return undefined;
    }
    if (!raw) {
        return undefined;
    }
    try {
        const parsed = JSON.parse(raw);
        if (typeof parsed.key !== 'string' || typeof parsed.version !== 'number') {
            return undefined;
        }
        return parsed;
    }
    catch {
        forgetHistoryKey();
        return undefined;
    }
}
function storeKey(material) {
    try {
        sessionStorage.setItem(KEY_STORAGE, JSON.stringify(material));
    }
    catch {
        // Sem persistir, cada aba paga uma ida a mais. Nao impede nada.
        LOG.info('Nao foi possivel guardar a chave do endereco nesta aba');
    }
}
//# sourceMappingURL=history-codec.js.map